/* eslint-disable @wyz/module/no-relative-import */
import { notify } from '@wyz/ui';
import * as React from 'react';
import { useApplicationContext } from '~/common/context';
import { PluginContextType } from '~/common/types';
import { PluginContext } from './contexts';

export const usePlugin = () => {
  const context = React.useContext(PluginContext);
  if (!context) {
    throw new Error('usePlugin must be used within PluginProvider');
  }
  return context;
};

type EnabledPlugins = {
  navbar: boolean;
  footer: boolean;
  sidebar: boolean;
};

type PluginState =
  | { status: 'idle' }
  | { status: 'loading'; scriptUrl: string }
  | {
      status: 'ready';
      enabledPlugins: EnabledPlugins;
      api: PluginContextType['api'];
    }
  | { status: 'error'; error: Error };

type PluginAction =
  | { type: 'LOAD'; scriptUrl: string }
  | { type: 'UNLOAD' }
  | { type: 'SCRIPT_LOADED' }
  | {
      type: 'PLUGIN_READY';
      enabledPlugins: EnabledPlugins;
      api: PluginContextType['api'];
    }
  | { type: 'ERROR'; error: Error };

type PluginProviderProps = {
  children: React.ReactNode;
};

const SCRIPT_ID = 'external-plugin-script';
const PLUGIN_CHECK_TIMEOUT = 50;
const PLUGIN_CHECK_MAX_ATTEMPTS = 100;

const pluginReducer = (
  state: PluginState,
  action: PluginAction,
): PluginState => {
  switch (action.type) {
    case 'LOAD':
      return { status: 'loading', scriptUrl: action.scriptUrl };
    case 'UNLOAD':
      return { status: 'idle' };
    case 'PLUGIN_READY':
      return {
        status: 'ready',
        enabledPlugins: action.enabledPlugins,
        api: action.api,
      };
    case 'ERROR':
      return { status: 'error', error: action.error };
    default:
      return state;
  }
};

const cleanupPlugin = () => {
  const existingScript = document.getElementById(SCRIPT_ID);
  existingScript?.remove();
  if (window.LayoutPlugin) {
    delete window.LayoutPlugin;
  }
};

// Authors note: Added cache busting to ensure fresh script load, this is also important for testing the feature
const addCacheBuster = (url: string): string => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_t=${Date.now()}`;
};

const getEnabledPlugins = (): EnabledPlugins => ({
  navbar: customElements.get('custom-navbar') !== undefined,
  footer: customElements.get('custom-footer') !== undefined,
  sidebar: customElements.get('custom-sidebar') !== undefined,
});

export const PluginProvider: React.FC<PluginProviderProps> = ({ children }) => {
  const { application } = useApplicationContext();
  const pluginScriptUrl = application?.pluginScriptUrl;

  const [state, dispatch] = React.useReducer(pluginReducer, { status: 'idle' });
  const [isTestingPlugin, setIsTestingPlugin] = React.useState(false);
  const [activeScriptUrl, setActiveScriptUrl] = React.useState<
    string | undefined
  >(pluginScriptUrl);

  const lastLoadedUrlRef = React.useRef<string | undefined>(undefined);

  const checkPluginReady = React.useCallback(() => {
    let attempts = 0;

    const check = () => {
      if (window.LayoutPlugin) {
        try {
          window.LayoutPlugin.define();
          dispatch({
            type: 'PLUGIN_READY',
            enabledPlugins: getEnabledPlugins(),
            api: {
              onAddToCart: window.LayoutPlugin?.onAddToCart,
            },
          });
        } catch (error) {
          dispatch({ type: 'ERROR', error: error as Error });
        }
      } else if (attempts < PLUGIN_CHECK_MAX_ATTEMPTS) {
        attempts++;
        setTimeout(check, PLUGIN_CHECK_TIMEOUT);
      } else {
        dispatch({
          type: 'ERROR',
          error: new Error('Plugin not available after loading script'),
        });
      }
    };

    check();
  }, []);

  const loadScript = React.useCallback(
    (scriptUrl: string) => {
      const script = document.createElement('script');
      script.src = addCacheBuster(scriptUrl);
      script.id = SCRIPT_ID;
      script.type = 'module';
      script.async = true;

      script.onload = () => checkPluginReady();
      script.onerror = () => {
        const error = new Error(`Failed to load external script: ${scriptUrl}`);
        notify.error(error.message, {
          id: 'toast-error-plugin-script-load-failed',
        });
        dispatch({ type: 'ERROR', error });
      };

      document.head.appendChild(script);
    },
    [checkPluginReady],
  );

  React.useEffect(() => {
    setActiveScriptUrl(pluginScriptUrl);
  }, [pluginScriptUrl]);

  React.useEffect(() => {
    if (!activeScriptUrl) {
      if (lastLoadedUrlRef.current) {
        cleanupPlugin();
        dispatch({ type: 'UNLOAD' });
        lastLoadedUrlRef.current = undefined;
      }
      return;
    }

    if (lastLoadedUrlRef.current === activeScriptUrl) {
      return;
    }

    if (lastLoadedUrlRef.current) {
      cleanupPlugin();
    }

    lastLoadedUrlRef.current = activeScriptUrl;
    dispatch({ type: 'LOAD', scriptUrl: activeScriptUrl });
    loadScript(activeScriptUrl);
  }, [activeScriptUrl, loadScript]);

  const testPlugin = React.useCallback((url: string) => {
    setIsTestingPlugin(true);
    setActiveScriptUrl(url);
  }, []);

  const resetPlugin = React.useCallback(() => {
    setIsTestingPlugin(false);
    setActiveScriptUrl(pluginScriptUrl);
  }, [pluginScriptUrl]);

  const contextValue = React.useMemo<PluginContextType>(() => {
    const baseState = {
      testPlugin,
      resetPlugin,
      isTestingPlugin,
      scriptUrl: activeScriptUrl,
      api: {},
    };

    switch (state.status) {
      case 'idle':
        return {
          ...baseState,
          isPluginReady: true,
          pluginError: null,
          enabledPlugins: { navbar: false, footer: false, sidebar: false },
        };
      case 'loading':
        return {
          ...baseState,
          isPluginReady: false,
          pluginError: null,
          enabledPlugins: { navbar: false, footer: false, sidebar: false },
        };
      case 'ready':
        return {
          ...baseState,
          isPluginReady: true,
          pluginError: null,
          enabledPlugins: state.enabledPlugins,
          api: state.api,
        };
      case 'error':
        return {
          ...baseState,
          isPluginReady: true,
          pluginError: state.error,
          enabledPlugins: { navbar: false, footer: false, sidebar: false },
        };
    }
  }, [state, testPlugin, resetPlugin, isTestingPlugin, activeScriptUrl]);

  return (
    <PluginContext.Provider value={contextValue}>
      {children}
    </PluginContext.Provider>
  );
};
